import { useTranslation } from 'react-i18next';

import { StatCardRow } from '@/modules/common/components/molecules/stat-card';
import { formatDisplayDate } from '@/utils/date-formatters';

import { getTimeOffTypeIcon } from '../../constants/time-off-type-icon-map';
import { TimeOffBalance } from '../../types/time-off-balance';

interface TimeOffRequestBalanceCardProps {
  balance: TimeOffBalance;
  title?: string;
  /** Number of days this request books — drives "Requested days" + the footer chip. */
  requestedDays: number;
  /** When true, shows a "Balance after booking" row (availableDays − requestedDays). */
  isPending: boolean;
  /** Booking-validation error (e.g. exceeds balance) — shown under the requested chip. */
  error?: string | null;
  /**
   * Renewal-aware predicted balance after booking (create/edit only), from the same
   * projection the backend uses to accept/reject the submission. Falls back to the naive
   * availableDays − requestedDays subtraction while the preview hasn't resolved yet, so the
   * row never renders blank.
   */
  previewedAvailableAfterBooking?: number | null;
}

export const TimeOffRequestBalanceCard = ({
  balance,
  title,
  requestedDays,
  isPending,
  error,
  previewedAvailableAfterBooking,
}: TimeOffRequestBalanceCardProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const Icon = getTimeOffTypeIcon(balance.type.code);

  const formatDaysValue = (value: number) => t('balances.daysValue', { count: value });
  const totalRequestedDays = balance.requestedDays ?? requestedDays;

  return (
    <div className="flex flex-col gap-4 rounded-xl border bg-surface p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-semibold">{title ?? t('details.balance.title')}</span>
        <span className="text-xs text-muted-foreground">
          {t('details.balance.today', { date: formatDisplayDate(new Date()) })}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-foreground text-background">
          <Icon className="h-4 w-4" />
        </div>
        <span className="truncate text-sm font-medium">{balance.type.displayName}</span>
      </div>

      <div className="flex flex-col gap-2">
        {/* "Total requested days" is hidden when it equals the chip below — the
            two would otherwise duplicate the same number and confuse the user. */}
        {totalRequestedDays !== requestedDays && (
          <StatCardRow
            label={t('details.balance.requestedDays')}
            value={formatDaysValue(totalRequestedDays)}
          />
        )}
        <StatCardRow
          label={t('details.balance.availableBalance')}
          value={formatDaysValue(balance.availableDays)}
        />
        {isPending && (
          <StatCardRow
            label={t('details.balance.balanceAfterBooking')}
            value={formatDaysValue(
              previewedAvailableAfterBooking != null
                ? Math.round(previewedAvailableAfterBooking * 10) / 10
                : Math.round((balance.availableDays - requestedDays) * 10) / 10
            )}
          />
        )}
      </div>

      <div className="rounded-lg bg-muted-foreground/15 py-2 text-center text-sm font-medium">
        {t('details.balance.requestedChip', { count: requestedDays })}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
